import React, { useState, useEffect } from 'react';
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const SimplePullToRefresh = ({ children, onRefresh, refreshMessage = '업데이트 완료!', showDataCount = false }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [newDataCount, setNewDataCount] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);

  const threshold = 100; // 민감도 낮춤 (80 → 100)

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        setStartY(e.touches[0].clientY);
        setIsPulling(true);
      }
    };

    const handleTouchMove = (e) => {
      if (!isPulling || isRefreshing) return;
      
      const currentY = e.touches[0].clientY;
      const distance = currentY - startY;
      
      if (window.scrollY === 0 && distance > 10) { // 10px 이상 당겨야 시작
        setPullDistance(Math.min(distance / 2.5, 120)); // 저항 증가
      }
    };

    const handleTouchEnd = async () => {
      if (pullDistance >= threshold && !isRefreshing) {
        setIsRefreshing(true);
        
        try {
          const result = await onRefresh();
          if (typeof result === 'number' && showDataCount) {
            setNewDataCount(result);
          }
          
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            setNewDataCount(0);
          }, 2000);
        } catch (error) {
          console.error('Refresh error:', error);
        } finally {
          setTimeout(() => {
            setIsRefreshing(false);
          }, 300);
        }
      }
      
      setPullDistance(0);
      setIsPulling(false);
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPulling, isRefreshing, pullDistance, startY, onRefresh, showDataCount]);

  const progress = Math.min((pullDistance / threshold) * 100, 100);
  const isReady = pullDistance >= threshold;

  return (
    <>
      {/* Pull 인디케이터 */}
      {(pullDistance > 0 || isRefreshing || showSuccess) && (
        <div 
          className="fixed top-0 left-0 right-0 flex items-center justify-center bg-white shadow-md z-50 transition-all duration-300"
          style={{
            height: `${Math.max(60, pullDistance)}px`,
            opacity: pullDistance > 20 ? 1 : 0 // 20px 이상일 때만 보임
          }}
        >
          <div className="relative">
            {showSuccess ? (
              <div className="text-center">
                <CheckCircleIcon className="h-6 w-6 text-green-600 mx-auto mb-1" />
                <p className="text-xs text-green-600 font-bold">
                  {refreshMessage}
                  {newDataCount > 0 && showDataCount && (
                    <span className="block">새 항목 {newDataCount}개!</span>
                  )}
                </p>
              </div>
            ) : isRefreshing ? (
              <div className="text-center">
                <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-1"></div>
                <p className="text-xs text-blue-600">업데이트 중...</p>
              </div>
            ) : (
              <div className="text-center">
                <WrenchScrewdriverIcon 
                  className={`h-6 w-6 mx-auto mb-1 transition-colors ${
                    isReady ? 'text-blue-600' : 'text-gray-400'
                  }`}
                  style={{
                    transform: `rotate(${progress * 3.6}deg) scale(${0.8 + progress * 0.002})`
                  }}
                />
                <p className={`text-xs ${isReady ? 'text-blue-600' : 'text-gray-400'}`}>
                  {isReady ? '놓으면 새로고침' : '아래로 당기세요'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 콘텐츠 */}
      <div 
        className="pull-to-refresh-content"
        style={{
          marginTop: pullDistance > 0 ? `${pullDistance}px` : '0',
          transition: isPulling ? 'none' : 'margin-top 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </>
  );
};

export default SimplePullToRefresh;